export const calculateShadeColor = (col: any, amt: any) => {
  col = col.replace(/^#/, "");
  if (col.length === 3)
    col = col[0] + col[0] + col[1] + col[1] + col[2] + col[2];

  let [r, g, b] = col.match(/.{2}/g);
  [r, g, b] = [
    parseInt(r, 16) + amt,
    parseInt(g, 16) + amt,
    parseInt(b, 16) + amt,
  ];

  r = Math.max(Math.min(255, r), 0).toString(16);
  g = Math.max(Math.min(255, g), 0).toString(16);
  b = Math.max(Math.min(255, b), 0).toString(16);

  const rr = (r.length < 2 ? "0" : "") + r;
  const gg = (g.length < 2 ? "0" : "") + g;
  const bb = (b.length < 2 ? "0" : "") + b;

  return `#${rr}${gg}${bb}`;
};

export const calculateContrast = (hex: any) => {
  try {
    var r = parseInt(hex.substring(1, 3), 16),
      g = parseInt(hex.substring(3, 5), 16),
      b = parseInt(hex.substring(5, 7), 16),
      yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 160 ? "#000000" : "#ffffff";
  } catch (exp) {
    return "#000000";
  }
};

export interface ThemeColors {
  primaryColor: string;
  backgroundColor: string;
  buttonColor: string;
  headerColor: string;
  headerColor2: string;
  headerColor3: string;
  getBorderRadius: (multiplier: number) => number;
  auroraColors?: string[];
}

export const buildThemeCss = (colors: ThemeColors): string => {
  const {
    primaryColor,
    backgroundColor,
    buttonColor,
    headerColor,
    headerColor2,
    headerColor3,
    getBorderRadius,
    auroraColors,
  } = colors;

  const contrastColor = calculateContrast(primaryColor);
  const contrastHeaderColor = calculateContrast(headerColor);
  const contrastButtonColor = calculateContrast(buttonColor);
  const contrastBackgroundColor = calculateContrast(backgroundColor);
  const isDarkMode = contrastBackgroundColor === "#ffffff";
  const headerDarkColor = calculateShadeColor(
    headerColor,
    contrastHeaderColor === "#ffffff" ? -35 : -15,
  );
  const subTextColor = isDarkMode
    ? calculateShadeColor(backgroundColor, 140)
    : calculateShadeColor(backgroundColor, -140);
  const backgroundColorHover = isDarkMode
    ? calculateShadeColor(backgroundColor, 28)
    : calculateShadeColor(backgroundColor, -12);
  const hoverHoverColor = isDarkMode
    ? calculateShadeColor(backgroundColor, 80)
    : calculateShadeColor(backgroundColor, -30);
  const borderColor = isDarkMode
    ? calculateShadeColor(backgroundColor, 200)
    : calculateShadeColor(backgroundColor, -70);
  // The agent/AI message bubble surface. Deliberately its own token rather than
  // --background-color-darker: that one is the interactive hover wash (composer
  // chips, list rows, and this bubble's OWN reply-quote hover), and the chatbar
  // re-points it to a translucent overlay. The dark step is intentionally
  // larger than the light one — a raised surface needs more separation on dark.
  const agentBubbleColor = isDarkMode
    ? calculateShadeColor(backgroundColor, 16)
    : calculateShadeColor(backgroundColor, -13);

  // Low-alpha washes of a surface's OWN text colour. They let a nested surface
  // (the reply quote inside a bubble) tint its parent without knowing the
  // parent's colour: over a dark brand bubble the white contrast lightens it,
  // over a light one the black contrast darkens it, by the same amount.
  // Surfaces that ship a fixed light palette (callouts, the danger button,
  // collapsible blocks, table borders, card outlines) read these with their light colour as
  // the var() fallback. They're only emitted on a dark background, so light
  // mode renders exactly as before. The light chatbar panel resets them to
  // `initial` (= fall back to the light colour) — see ChatbarView.scss.
  const darkSurfaceVars = isDarkMode
    ? `
        --callout-info-bg: rgba(0, 123, 255, 0.16);
        --callout-info-text: #9ecbff;
        --callout-warning-bg: rgba(255, 193, 7, 0.16);
        --callout-warning-text: #ffd866;
        --callout-error-bg: rgba(220, 53, 69, 0.18);
        --callout-error-text: #ffa3ad;
        --callout-success-bg: rgba(40, 167, 69, 0.18);
        --callout-success-text: #8fe0a2;
        --danger-button-bg: transparent;
        --danger-button-bg-hover: rgba(220, 38, 38, 0.16);
        --danger-button-text: #f87171;
        --danger-button-border: rgba(248, 113, 113, 0.5);
        --danger-button-border-hover: #f87171;
        --details-border-color: rgba(255, 255, 255, 0.15);
        --details-hover-color: rgba(255, 255, 255, 0.05);
        --details-button-hover-color: rgba(255, 255, 255, 0.12);
        --table-border-color: rgba(255, 255, 255, 0.12);
        --card-shadow: rgba(255, 255, 255, 0.12) 0px 0px 0px 1px,
          rgba(0, 0, 0, 0.3) 0px 2px 8px;`
    : "";

  const auroraVars =
    Array.isArray(auroraColors) && auroraColors.length === 5
      ? auroraColors
          .map((c, i) => `        --aurora-c${i + 1}: ${c};`)
          .join("\n")
      : "";

  return `
    :root {
        --primary-color: ${primaryColor};
        --primary-color-AA: ${primaryColor}AA;
        --primary-color-20: ${primaryColor}20;
        --primary-color-55: ${primaryColor}55;
        --primary-color-44: ${primaryColor}44;
        --primary-color-22: ${primaryColor}22;
        --primary-color-11: ${primaryColor}11;
        --primary-color-07: ${primaryColor}07;
        --header-color: ${headerColor};
        --header-color-2: ${headerColor2};
        --header-color-3: ${headerColor3};
        --header-dark-color: ${headerDarkColor};
        --button-color: ${buttonColor};
        --background-color: ${backgroundColor};
        --background-color-99: ${backgroundColor}99;
        --background-color-EE: ${backgroundColor}ee;
        --background-color-1A: ${backgroundColor}1A;
        --background-color-darker: ${backgroundColorHover};
        --background-color-dark: ${hoverHoverColor};
        --agent-bubble-color: ${agentBubbleColor};
        --primary-contrast-color: ${contrastColor};
        --primary-contrast-color-1A: ${contrastColor}1A;
        --primary-contrast-color-26: ${contrastColor}26;
        --primary-contrast-color-99: ${contrastColor}99;
        --header-contrast-color: ${contrastHeaderColor};
        --header-contrast-color-99: ${contrastHeaderColor}99;
        --header-contrast-color-33: ${contrastHeaderColor}33;
        --button-contrast-color: ${contrastButtonColor};
        --primary-text-color: ${contrastBackgroundColor};
        --primary-text-color-99: ${contrastBackgroundColor}99;
        --primary-text-color-54: ${contrastBackgroundColor}54;
        --primary-text-color-1A: ${contrastBackgroundColor}1A;
        --primary-text-color-11: ${contrastBackgroundColor}11;
        --secondary-text-color: ${subTextColor};
        --secondary-text-color99: ${subTextColor}99;
        --border-color: ${borderColor};
        --border-color-33: ${borderColor}33;
        --border-color-55: ${borderColor}55;
        --border-color-11: ${borderColor}11;
        --border-radius: ${getBorderRadius(1.0)}px;
        --border-radius-150: ${getBorderRadius(1.5)}px;
        --border-radius-60: ${getBorderRadius(0.6)}px;
        --border-radius-50: ${getBorderRadius(0.5)}px;
        --border-radius-40: ${getBorderRadius(0.4)}px;
        --border-radius-25: ${getBorderRadius(0.25)}px;
        --border-radius-15: ${getBorderRadius(0.15)}px;
        --conversation-gutter: 16px;${darkSurfaceVars}
${auroraVars}
    }

    /* Native controls, scrollbars and autofill follow the theme. Set on body,
       not :root — a root color-scheme that differs from the host page's makes
       the browser paint this (transparent) iframe opaque. */
    body {
        color-scheme: ${isDarkMode ? "dark" : "light"};
    }
    `;
};
